import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { config } from '../config';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // Unix timestamp in ms
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
  getValidToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Buffer time before expiration to refresh (60 seconds)
const REFRESH_BUFFER_MS = 60 * 1000;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    isAuthenticated: false,
  });

  // Use ref to avoid stale closures in refresh logic
  const authStateRef = useRef(authState);
  authStateRef.current = authState;

  // Track if a refresh is in progress to avoid concurrent refreshes
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const setTokens = useCallback((tokens: { accessToken: string; refreshToken?: string; expiresIn: number }) => {
    const expiresAt = Date.now() + tokens.expiresIn * 1000;
    const newState = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken || authStateRef.current.refreshToken,
      expiresAt,
      isAuthenticated: true,
    };
    setAuthState(newState);

    // Persist to localStorage
    localStorage.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    localStorage.setItem('expiresAt', expiresAt.toString());
  }, []);

  const logout = useCallback(() => {
    setAuthState({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      isAuthenticated: false,
    });
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const currentRefreshToken = authStateRef.current.refreshToken;
    if (!currentRefreshToken) {
      console.log('No refresh token available');
      logout();
      return null;
    }

    try {
      console.log('Refreshing access token...');
      const response = await fetch(`${config.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) {
        console.error('Token refresh failed:', response.status);
        logout();
        return null;
      }

      const data = await response.json();
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken, // Keycloak may return a new refresh token
        expiresIn: data.expiresIn,
      });

      console.log('Token refreshed successfully');
      return data.accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      return null;
    }
  }, [logout, setTokens]);

  // Get a valid token, refreshing if necessary
  const getValidToken = useCallback(async (): Promise<string | null> => {
    const { accessToken, expiresAt } = authStateRef.current;

    // Not authenticated
    if (!accessToken) {
      return null;
    }

    // Check if token is still valid (with buffer)
    const now = Date.now();
    if (expiresAt && now < expiresAt - REFRESH_BUFFER_MS) {
      return accessToken;
    }

    // Token is expired or about to expire, need to refresh
    console.log('Token expired or expiring soon, refreshing...');

    // Avoid concurrent refresh calls
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = refreshAccessToken().finally(() => {
      refreshPromiseRef.current = null;
    });

    return refreshPromiseRef.current;
  }, [refreshAccessToken]);

  const login = useCallback(async () => {
    try {
      // Open popup FIRST (synchronously) to avoid iOS Safari blocking
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open a blank popup immediately (before async operations)
      // Use unique name to avoid browser reusing a cached/stale popup window
      const popup = window.open(
        'about:blank',
        `auth_${Date.now()}`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert('Please allow popups for this site to sign in.');
        return;
      }

      // Show loading message in popup
      popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;"><div>Loading authentication...</div></body></html>');

      // Now get auth URL from server (async)
      const response = await fetch(`${config.apiUrl}/auth/init`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Auth init failed:', response.status, errorData);
        popup.close();
        alert(`Login failed (${response.status}): ${errorData.error || 'Server error'}. Check server logs.`);
        return;
      }

      const data = await response.json();

      if (!data.authUrl) {
        console.error('Auth init returned no authUrl:', data);
        popup.close();
        alert('Login failed: no auth URL returned. Check server logs.');
        return;
      }

      // Navigate the popup to the auth URL
      popup.location.href = data.authUrl;
    } catch (error) {
      console.error('Failed to initiate login:', error);
      alert('Failed to start login process. Please try again.');
    }
  }, []);

  // Listen for auth completion from popup (via postMessage or localStorage)
  useEffect(() => {
    // postMessage handler (works when window.opener is available)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'auth_complete' && event.data.success) {
        setTokens({
          accessToken: event.data.tokens.accessToken,
          refreshToken: event.data.tokens.refreshToken,
          expiresIn: event.data.tokens.expiresIn,
        });
      }
    };

    // localStorage handler (works when popup navigates cross-origin and loses window.opener)
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'auth_complete' && event.newValue) {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        const expiresAtStr = localStorage.getItem('expiresAt');
        if (accessToken && refreshToken && expiresAtStr) {
          const expiresAt = parseInt(expiresAtStr, 10);
          setAuthState({
            accessToken,
            refreshToken,
            expiresAt,
            isAuthenticated: true,
          });
          authStateRef.current = { accessToken, refreshToken, expiresAt, isAuthenticated: true };
        }
        // Clean up the signal key
        localStorage.removeItem('auth_complete');
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [setTokens]);

  // Check for existing tokens on mount and validate them
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const expiresAtStr = localStorage.getItem('expiresAt');

    if (accessToken && refreshToken) {
      const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null;
      const now = Date.now();

      // If access token is still valid, restore the session
      if (expiresAt && now < expiresAt - REFRESH_BUFFER_MS) {
        setAuthState({
          accessToken,
          refreshToken,
          expiresAt,
          isAuthenticated: true,
        });
      } else {
        // Access token expired — try to refresh immediately
        authStateRef.current = { accessToken, refreshToken, expiresAt, isAuthenticated: false };
        refreshAccessToken().then((newToken) => {
          if (!newToken) {
            // Refresh failed — clear stale tokens
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('expiresAt');
          }
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        getValidToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
